import { schemaResolver, useForm } from '@mantine/form';
import { requiredText } from '../utils/formValidation.ts';
import { z } from 'zod';
import { NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { VEHICLE_CLASS_OPTIONS } from '../constants/fieldOptions.ts';
import { RocDateInput } from './RocDateInput.tsx';
import type {
  ApproveVehicleRegistrationInfo,
  VehicleClass,
  VehicleRegistrationInfo,
} from '../model.ts';

type VehicleRegistrationFormValues = {
  plateNumber: string;
  vehicleClass: string;
  ownerName: string;
  ownerAddress: string;
  printNo: string;
  brandModel: string;
  vin: string;
  color: string;
  displacementCc: string | number;
  manufactureYearMonth: string | null;
  issueDate: string | null;
  reissueDate: string | null;
};

type VehicleRegistrationFormProps = {
  formId: string;
  initialInfo?: VehicleRegistrationInfo;
  readOnly?: boolean;
  onValidSubmit: (info: ApproveVehicleRegistrationInfo) => void;
};

const trimToNull = (value: string) => value.trim() || null;

const READABLE_DISABLED_STYLES = {
  input: {
    color: 'var(--mantine-color-text)',
    opacity: 1,
  },
} as const;

// MonthPickerInput 的值是 YYYY-MM-DD，API 要的是 YYYY-MM
const toMonthValue = (value: string | null) => value?.slice(0, 7) ?? null;
const fromMonthValue = (value: string | undefined) =>
  value ? `${value}-01` : null;

const buildInitialValues = (
  info?: VehicleRegistrationInfo,
): VehicleRegistrationFormValues => ({
  plateNumber: info?.plateNumber ?? '',
  vehicleClass: info?.vehicleClass ?? '',
  ownerName: info?.ownerName ?? '',
  ownerAddress: info?.ownerAddress ?? '',
  printNo: info?.printNo ?? '',
  brandModel: info?.brandModel ?? '',
  vin: info?.vin ?? '',
  color: info?.color ?? '',
  displacementCc: info?.displacementCc ?? '',
  manufactureYearMonth: fromMonthValue(info?.manufactureYearMonth ?? undefined),
  issueDate: info?.issueDate ?? null,
  reissueDate: info?.reissueDate ?? null,
});

const VehicleRegistrationFormSchema = z.object({
  plateNumber: requiredText('請輸入車牌號碼'),
  vehicleClass: requiredText('請選擇行照類型'),
  ownerName: requiredText('請輸入姓名'),
  ownerAddress: requiredText('請輸入地址'),
});

const VehicleRegistrationForm = ({
  formId,
  initialInfo,
  readOnly = false,
  onValidSubmit,
}: VehicleRegistrationFormProps) => {
  const form = useForm<VehicleRegistrationFormValues>({
    initialValues: buildInitialValues(initialInfo),
    validate: schemaResolver(VehicleRegistrationFormSchema, { sync: true }),
  });

  const handleSubmit = (values: VehicleRegistrationFormValues) => {
    onValidSubmit({
      plateNumber: values.plateNumber.trim(),
      vehicleClass: values.vehicleClass as VehicleClass,
      ownerName: values.ownerName.trim(),
      ownerAddress: values.ownerAddress.trim(),
      printNo: trimToNull(values.printNo),
      brandModel: trimToNull(values.brandModel),
      vin: trimToNull(values.vin),
      color: trimToNull(values.color),
      displacementCc:
        values.displacementCc === '' ? null : Number(values.displacementCc),
      manufactureYearMonth: toMonthValue(values.manufactureYearMonth),
      issueDate: values.issueDate,
      reissueDate: values.reissueDate,
    });
  };

  return (
    <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        <TextInput
          label="車牌號碼"
          placeholder="請輸入車牌號碼"
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('plateNumber')}
        />
        <Select
          label="行照類型"
          placeholder="請選擇行照類型"
          data={VEHICLE_CLASS_OPTIONS}
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('vehicleClass')}
        />
        <TextInput
          label="姓名"
          placeholder="請輸入姓名"
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('ownerName')}
        />
        <TextInput
          label="地址"
          placeholder="請輸入地址"
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('ownerAddress')}
        />
        <TextInput
          label="印製號碼"
          placeholder="請輸入印製號碼"
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('printNo')}
        />
        <TextInput
          label="廠牌型號"
          placeholder="請輸入廠牌型號"
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('brandModel')}
        />
        <TextInput
          label="車身號碼"
          placeholder="請輸入車身號碼"
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('vin')}
        />
        <TextInput
          label="顏色"
          placeholder="請輸入顏色"
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('color')}
        />
        <NumberInput
          label="排氣量"
          placeholder="請輸入排氣量"
          suffix=" cc"
          min={0}
          allowDecimal={false}
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('displacementCc')}
        />
        <MonthPickerInput
          label="出廠年月"
          placeholder="請選擇出廠年月"
          valueFormat="YYYY/MM"
          clearable
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('manufactureYearMonth')}
        />
        <RocDateInput
          label="發照日期"
          placeholder="請選擇發照日期"
          clearable
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('issueDate')}
        />
        <RocDateInput
          label="補換發日期"
          placeholder="請選擇補換發日期"
          clearable
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('reissueDate')}
        />
      </Stack>
    </form>
  );
};

export { VehicleRegistrationForm };
